import { readChatStore, writeChatStore } from './chatStore';

// One instance per signed-in chat screen. With `persistFor` (the user id) the last few threads are
// also saved in the browser (utils/chatStore.js) and restored on the next page load: shown at once,
// but treated as stale, so each is refreshed from the server as soon as it is opened.
export const CHAT_PAGE_SIZE = 20;
const MAX_CHATS = 10;
const FRESH_MS = 60000;

export const mergeMessages = (...pages) => [...new Map(
    pages.flat().map(message => [String(message._id), message])
).values()].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    || String(a._id).localeCompare(String(b._id)));

export const createChatCache = (api, { persistFor = null } = {}) => {
    const entries = new Map();
    let activeId = null;

    for (const [id, saved] of readChatStore(persistFor, 'threads') || []) {
        if (saved?.conversation || saved?.messages) {
            entries.set(String(id), { ...saved, revision: 0, messagesAt: 0, conversationAt: 0 });
        }
    }
    let saveTimer = null;
    // Batched: a burst of socket events and loads becomes one write.
    const scheduleSave = () => {
        if (!persistFor || saveTimer) return;
        saveTimer = setTimeout(() => {
            saveTimer = null;
            writeChatStore(persistFor, 'threads', [...entries.entries()]
                .filter(([, entry]) => entry.conversation || entry.messages)
                .map(([id, entry]) => [id, { conversation: entry.conversation, messages: entry.messages }]));
        }, 1000);
    };

    const entryFor = (id) => {
        const key = String(id);
        let entry = entries.get(key);
        if (!entry) entry = { revision: 0, messagesAt: 0, conversationAt: 0 };
        entries.delete(key);
        entries.set(key, entry);
        while (entries.size > MAX_CHATS) {
            const keepId = activeId;
            const victim = [...entries.keys()].find(candidate => candidate !== keepId);
            entries.delete(victim);
        }
        return entry;
    };
    const load = async (id, field) => {
        const entry = entryFor(id);
        if (entry[field] && Date.now() - entry[`${field}At`] < FRESH_MS) return entry[field];
        const pending = `${field}Pending`;
        if (entry[pending]) return entry[pending];
        const revision = entry.revision;
        const request = api.get(`/conversations/${id}${field === 'messages' ? '/messages' : ''}`,
            field === 'messages' ? { params: { limit: CHAT_PAGE_SIZE } } : undefined);
        entry[pending] = request.then(async ({ data }) => {
            // A clear, removal or newer socket event must win over an old GET.
            if (entries.get(String(id)) !== entry) return data;
            if (entry.revision !== revision) {
                entry[pending] = null;
                return load(id, field);
            }
            entry[field] = data;
            entry[`${field}At`] = Date.now();
            scheduleSave();
            return data;
        }).finally(() => { entry[pending] = null; });
        return entry[pending];
    };
    return {
        peek: (id) => entries.get(String(id)),
        setActive(id) { activeId = id ? String(id) : null; },
        loadMessages: (id) => load(id, 'messages'),
        loadConversation: (id) => load(id, 'conversation'),
        writeMessages(id, messages, hasMore) {
            const entry = entryFor(id);
            entry.revision++;
            entry.messages = { messages: messages.slice(-CHAT_PAGE_SIZE), hasMore: hasMore || messages.length > CHAT_PAGE_SIZE };
            entry.messagesAt = Date.now();
            scheduleSave();
        },
        invalidate(id, { conversation = false, remove = false } = {}) {
            const entry = entries.get(String(id));
            if (!entry) return;
            entry.revision++;
            entry.messagesAt = 0;
            if (conversation) entry.conversationAt = 0;
            if (remove) {
                entries.delete(String(id));
                scheduleSave();
            }
        },
        invalidateAll() {
            for (const entry of entries.values()) {
                entry.revision++;
                entry.messagesAt = 0;
                entry.conversationAt = 0;
            }
        },
        async prefetch(ids, cancelled = () => false) {
            const queue = [...new Set([activeId, ...ids.map(String)].filter(Boolean))].slice(0, MAX_CHATS);
            // Two background chats at a time, leaving room for the open thread.
            await Promise.all([0, 1].map(async () => {
                while (queue.length && !cancelled()) {
                    const id = queue.shift();
                    await Promise.allSettled([load(id, 'conversation'), load(id, 'messages')]);
                }
            }));
        }
    };
};
